import React, {
  createElement,
  MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { useDynamicScrolling } from './hooks/useDynamicScrolling';
import { useDynamicSizing } from './hooks/useDynamicSizing';
import { ItemMeasurer } from './ItemMeasurer';
import { OnItemsRenderedCallback, ReactElementType, RenderComponent } from './types';
import {
  getEstimatedTotalSize,
  getItemOffset,
  getItemSize,
  getNearestViewportOffsetForNeighboringItemIndex,
  getStartIndexForOffset,
  getStopIndexForStartIndex,
} from './utils';

const DEFAULT_ESTIMATED_ITEM_SIZE = 50;
const defaultItemKey = (index: number) => index;

const itemStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
};

export interface DynamicListHandles {
  forceUpdate(): void;
  /**
   * Scrolls the list to the provided offset.
   *
   * Although the list will scroll exactly to the provided offset, the scroll
   * operation should still be considered an approximation. Because of how the
   * virtualization works, a given offset can correspond to different points in
   * the list based on a number of factors including:
   * - Accuracy of `estimatedItemSize`
   * - How many and which items are expanded
   * - Which items have been measured
   *
   * Because of the roughness of this scrolling, this method should not be used
   * to emulate scrolling to a specific item/index.
   */
  scrollToOffset: (offset: number) => void;
  /**
   * This function instructs the list to scroll the item at the given index into view if the index is just outside the frame.
   * This is useful for keyboard navigation scenarios where the user is moving through rows one at a time. It is not
   * supported as a method to scroll to an arbitrary index, as variable row heights make estimating the offset required
   * for items not yet rendered unreliable.
   *
   * !IMPORTANT! Index should always increment or decrement by 1 when using this function. This ensures the requested index
   * has already been rendered. If the index is not yet rendered, this function will have no effect.
   *
   * @param neighboringItemIndex The index of the neighboring item that should be visible after scroll. Should only be above
   * or below the current view by 1.
   */
  scrollNeighboringItemIntoViewIfNecessary(neighboringItemIndex: number): void;
}

export interface Props<T = unknown> {
  /**
   * React component responsible for rendering the individual item specified by an index prop.
   * This component also receives a style prop (used for positioning), and a data prop that is passed in via itemData.
   *
   * If useIsScrolling is enabled for the list, the component also receives an additional isScrolling boolean prop.
   */
  children: RenderComponent;
  /**
   * Optional CSS class to attach to outermost <div> element.
   */
  className?: string;
  /**
   * Whether to delay when `initialScrollOffset` will be applied.
   *
   * For example, this can be used to wait to scroll until data has been fetched
   * by setting this equal to whether data is loading.
   *
   * Defaults to `false`. Has no effect if `initialScrollOffset` is not
   * supplied.
   */
  deferInitialScroll?: boolean;
  /**
   * Height of the list(in pixels).
   *
   * This must be a number. It affects the number of rows that will be rendered (and displayed) at any given time.
   */
  height: number;
  /**
   * Scroll offset for initial render.
   * This affects scrollTop.
   */
  initialScrollOffset?: number;
  /**
   * Ref to attach to the inner container element.
   */
  innerRef?: React.Ref<unknown>;
  /**
   * Tag name or React component passed to React.createElement to create the inner container element.
   */
  innerElementType?: ReactElementType | null;
  /**
   * Total number of items in the list. Note that only a few items will be rendered and displayed at a time.
   */
  itemCount: number;
  /**
   * Contextual data to be passed to the item renderer as a data prop.
   * This is a light-weight alternative to React's built-in context API.
   */
  itemData?: T;
  /**
   * By default, lists will use an item's index as its key. This is okay if:
   *
   * - Your collections of items is never sorted or modified
   * - Your item renderer is not stateful and does not extend PureComponent
   *
   * If your list does not satisfy the above constraints, use the itemKey property to specify your own keys for items.
   */
  itemKey?: (index: number, data: T) => string | number;
  /**
   * Called when the items rendered by the list change.
   */
  onItemsRendered?: OnItemsRenderedCallback;
  /**
   * Called when the list is scrolled. This is NOT debounced so use with caution.
   */
  onScroll?: (offset: number) => void;
  /**
   * Ref to attach to the outer container element.
   */
  outerRef?: React.Ref<unknown>;
  /**
   * Tag name or React component passed to React.createElement to create the outer container element.
   */
  outerElementType?: ReactElementType | null;
  /**
   * The number of items (rows or columns) to render outside of the visible area. This property can be important for two reasons:
   *
   * - Overscanning by one row or column allows the tab key to focus on the next (not yet visible) item.
   * - Overscanning slightly can reduce or prevent a flash of empty space when a user first starts scrolling.
   *
   * Note that overscanning too much can negatively impact performance. By default, DynamicList overscans by 4 items.
   */
  overscanCount?: number;
  /**
   * Optional inline style to attach to outermost <div> element.
   */
  style?: Record<string, string | number>;
  /**
   * Adds an additional isScrolling parameter to the children render function.
   * This parameter can be used to show a placeholder row or column while the list is being scrolled.
   *
   * Note that using this parameter will result in an additional render
   * call after scrolling has stopped (when isScrolling changes from true to false).
   */
  useIsScrolling?: boolean;
  /**
   * Width of the list.
   *
   * This can be a number or a string (e.g. "50%").
   */
  width: number | string;
  /**
   * Estimated size of a item in the direction being windowed.
   *
   * This value is used to calculated the estimated total size of a list before its items have all been measured.
   * The total size impacts user scrolling behavior.
   * It is updated whenever new items are measured.
   */
  estimatedItemSize?: number;

  /**
   * Define the ARIA Role of list container for use with screen readers.
   */
  role?: React.HTMLAttributes<HTMLDivElement>['role'];
}

const DynamicListInner = <T = unknown>(
  {
    onItemsRendered,
    onScroll: onScrollProp,
    estimatedItemSize = DEFAULT_ESTIMATED_ITEM_SIZE,
    initialScrollOffset = 0,
    itemData,
    itemKey = defaultItemKey,
    overscanCount = 4,
    useIsScrolling = false,
    deferInitialScroll = false,
    itemCount,
    height,
    children,
    className,
    innerRef,
    outerRef: outerRefProp,
    innerElementType,
    outerElementType,
    style,
    width,
    role,
  }: Props<T>,
  ref: React.Ref<DynamicListHandles>,
) => {
  const { isScrolling, scrollDirection, scrollOffset, onScroll } = useDynamicScrolling({
    onScroll: onScrollProp,
  });
  const { handleNewMeasurements, sizingFields, setSizeValues } = useDynamicSizing({
    itemCount,
    estimatedItemSize,
    scrollOffset,
  });
  const outerRef = useRef<HTMLElement | null>(null);
  const onItemsRenderedRef = useRef(onItemsRendered);

  const outerRefSetter = useCallback(
    (outerElemRef: HTMLElement): void => {
      outerRef.current = outerElemRef;
      if (typeof outerRefProp === 'function') {
        outerRefProp(outerElemRef);
      } else if (outerRefProp && 'current' in outerRefProp) {
        // eslint-disable-next-line no-param-reassign -- mutable refs are mutable by design
        (outerRefProp as MutableRefObject<unknown>).current = outerElemRef;
      }
    },
    [outerRefProp],
  );

  const { sizes } = sizingFields;
  // if sizes have not been calculated yet, generate initial sizes;
  if (itemCount !== sizes.getCount()) {
    setSizeValues();
  }

  const getRangeToRender = () => {
    if (itemCount === 0) {
      return [0, 0, 0, 0];
    }
    const startIndex = getStartIndexForOffset({
      itemCount,
      scrollOffset,
      sizes: sizingFields.sizes,
    });

    const stopIndex = getStopIndexForStartIndex({
      itemCount,
      scrollOffset,
      startIndex,
      height,
      sizes: sizingFields.sizes,
    });

    // Overscan by at least one item in each direction so that tab/focus works.
    // If there isn't at least one extra item, tab loops back around.
    const overscanBackward =
      !isScrolling || scrollDirection === 'backward' ? Math.max(1, overscanCount) : 1;
    const overscanForward =
      !isScrolling || scrollDirection === 'forward' ? Math.max(1, overscanCount) : 1;

    return [
      Math.max(0, startIndex - overscanBackward),
      Math.max(0, Math.min(itemCount - 1, stopIndex + overscanForward)),
      startIndex,
      stopIndex,
    ];
  };
  const [overscanStartIndex, overscanStopIndex, visibleStartIndex, visibleStopIndex] =
    getRangeToRender();

  useEffect(() => {
    if (typeof onItemsRenderedRef.current === 'function' && itemCount > 0) {
      onItemsRenderedRef.current({
        overscanStartIndex,
        overscanStopIndex,
        visibleStartIndex,
        visibleStopIndex,
      });
    }
  }, [itemCount, overscanStartIndex, overscanStopIndex, visibleStartIndex, visibleStopIndex]);

  const scrollToOffset = useCallback(
    (offset: number) => {
      if (!outerRef.current || offset === scrollOffset) return;

      outerRef.current.scrollTop = offset;
    },
    [scrollOffset],
  );

  useImperativeHandle(ref, () => ({
    // needed for react-window-infinite-loader to call forceUpdate.
    // No longer need to implement as we dont need to force an update
    // to get the latest sizes. Keeping for now so we dont break the api.
    // Will be updated when we swap SRL and DataGrid to use this component.
    forceUpdate: () => {},
    scrollToOffset,
    scrollNeighboringItemIntoViewIfNecessary: (index: number) => {
      const offset = getNearestViewportOffsetForNeighboringItemIndex({
        height,
        neighboringItemIndex: index,
        itemCount,
        scrollOffset,
        sizes,
        startIndex: visibleStartIndex,
        stopIndex: visibleStopIndex,
      });
      scrollToOffset(offset);
    },
  }));

  const hasScrolledInitiallyRef = useRef(false);
  const isReadyToDoInitialScroll =
    !deferInitialScroll &&
    // If the provided height is coming from a measured element, it may be zero or
    // even negative (if arithmetic is performed on the measured height) until
    // after that element has mounted. In that case, wait until a meaningful
    // height is provided.
    (height > 0 || (height === 0 && itemCount === 0));

  useEffect(() => {
    if (isReadyToDoInitialScroll && !hasScrolledInitiallyRef.current) {
      hasScrolledInitiallyRef.current = true;

      if (initialScrollOffset) {
        scrollToOffset(initialScrollOffset);
      }
    }
  }, [isReadyToDoInitialScroll, initialScrollOffset, scrollToOffset]);

  const items = [];
  const itemContainerStyle = {
    position: 'absolute',
    top: 0,
    width: '100%',
  };

  if (itemCount > 0) {
    const { sizes: latestSizes } = sizingFields;
    const offset = getItemOffset({ sizes: latestSizes, index: overscanStartIndex });
    itemContainerStyle.top = offset;
    for (let index = overscanStartIndex; index <= overscanStopIndex; index += 1) {
      const size = getItemSize({ index, sizes: latestSizes });

      // This is where the children component is rendered with argumements
      const item = createElement(children, {
        index,
        data: itemData,
        isScrolling: useIsScrolling ? isScrolling : undefined,
        offset,
        style: itemStyle,
      });

      // Always wrap children in a ItemMeasurer to detect changes in size.
      items.push(
        createElement(ItemMeasurer, {
          size,
          handleNewMeasurements,
          index,
          item,
          key: itemKey(index, itemData as T),
        }),
      );
    }
  }
  const renderedItems = createElement(
    'div',
    {
      style: itemContainerStyle,
      role: 'presentation',
    },
    items,
  );

  const estimatedTotalSize = getEstimatedTotalSize({
    itemCount,
    sizes: sizingFields.sizes,
  });

  return createElement(
    outerElementType || 'div',
    {
      className,
      onScroll,
      ref: outerRefSetter,
      role,
      style: {
        height,
        width,
        overflow: 'auto',
        position: 'relative',
        WebkitOverflowScrolling: 'touch',
        // `willChange: 'transform'` is set in the react-virtualized code that inspires this block.
        // Excluding from FF here as it caused issues with scrolling in FF 83.0.
        willChange:
          window.navigator.userAgent.toLowerCase().indexOf('firefox') > -1
            ? undefined
            : 'transform',
        ...style,
      },
    },
    createElement(
      innerElementType || 'div',
      {
        ref: innerRef,
        role: 'presentation',
        style: {
          height: estimatedTotalSize,
          pointerEvents: isScrolling ? 'none' : undefined,
          width: '100%',
        },
      },
      renderedItems,
    ),
  );
};

export const DynamicList = React.memo(React.forwardRef(DynamicListInner)) as <T extends unknown>(
  props: Props<T> & { ref?: React.Ref<DynamicListHandles> },
) => JSX.Element;
